(() => {
  'use strict';
  const create = (catalog, runtime = {}) => {
    const live = new Map((runtime.variants || []).map(v => [v.sku, v]));
    const variants = new Map(catalog.variants.map(v => [v.key, runtime.storefront
      ? { ...v, id: null, price: null, available: false, ...live.get(v.sku) }
      : { ...v, available: false }]));
    const colorMap = new Map(catalog.palette.map(v => [v.id, v]));
    const currency = runtime.currency || catalog.currency;
    const variant = key => {
      const value = variants.get(key);
      if (!value || !Number.isSafeInteger(value.id) || value.id < 1) throw new Error('This selection is not available yet. Your choices are saved.');
      if (!Number.isSafeInteger(value.price) || value.price < 0) throw new Error('The price could not be confirmed. Please refresh and try again.');
      return value;
    };
    const quantity = value => {
      if (!Number.isSafeInteger(value) || value < 1) throw new Error('Choose a whole-number quantity of at least 1.');
      return value;
    };
    const color = id => {
      const value = colorMap.get(id);
      if (!value) throw new Error('Please choose a color for every module.');
      return value;
    };
    const textStyle = input => {
      const rule = catalog.text;
      const text = String(input.text ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
      const font = rule.fonts.find(v => v.id === input.font);
      const textColor = rule.colors.find(v => v.id === input.color);
      if (!text || Array.from(text).length > rule.maxLength) throw new Error(`Enter custom text of up to ${rule.maxLength} characters.`);
      if (!font || !textColor || !['s', 'm', 'l'].includes(input.size) || !['left', 'center', 'right'].includes(input.align) || !['none', 'black', 'white'].includes(input.outline)) throw new Error('Please check your custom text settings.');
      return { text, font, color: textColor, size: input.size.toUpperCase(), align: input.align, outline: input.outline, referenceHeightMm: rule.referenceHeightMm };
    };
    const addTextProperties = (properties, custom) => {
      properties['Custom text'] = custom.text;
      properties['Text color'] = custom.color.label;
      properties.Font = custom.font.label;
      properties['Font size'] = custom.size;
      properties.Alignment = { left: 'Left', center: 'Center', right: 'Right' }[custom.align];
      properties.Outline = { none: 'None', black: 'Black', white: 'White' }[custom.outline];
    };
    const caseCustomization = item => {
      const mode = item.customizationMode ?? (item.customText ? 'text' : 'default');
      if (!['default', 'blank', 'text'].includes(mode) || (mode === 'text') !== Boolean(item.customText)) throw new Error('Please check your backplate customization.');
      return mode;
    };
    const caseKey = item => `case:${item.chinStyle === 'long' ? 'long' : 'short'}:${caseCustomization(item) === 'default' ? 'standard' : 'custom'}`;
    const moduleKey = item => `${item.module}:${item.module === 'magsafe' ? 'standard' : item.color}${item.customText ? ':custom' : ''}`;
    const build = snapshot => {
      const item = snapshot.case;
      if (!item || !['short', 'long'].includes(item.chinStyle)) throw new Error('Please choose a chin style.');
      const selected = variant(caseKey(item));
      const customizationMode = caseCustomization(item);
      const properties = { Model: 'Titan 2 Elite', 'Chin style': item.chinStyle === 'long' ? 'Extended Chin' : 'Chin', Customization: { default: 'Default smallsmore', blank: 'Blank backplate', text: 'Custom text' }[customizationMode] };
      const partColors = {};
      for (const part of catalog.parts) {
        const chosen = color(item.colors?.[part.id]);
        properties[part.label] = chosen.label;
        partColors[part.id] = { ...chosen };
      }
      const custom = item.customText ? textStyle(item.customText) : null;
      if (custom) addTextProperties(properties, custom);
      if (item.backPreviewUrl) {
        const preview = new URL(item.backPreviewUrl);
        if (preview.protocol !== 'https:' || preview.username || preview.password) throw new Error('The back preview link could not be confirmed.');
        properties['_Back preview'] = preview.href;
      } else if (item.backPreviewMissing) {
        properties['_Back preview status'] = 'Not saved';
      }
      properties._smallsmore_mcase_configuration = JSON.stringify({ schemaVersion: 1, model: 'Titan 2 Elite', sku: selected.sku, chinStyle: properties['Chin style'], parts: partColors, text: custom, defaultBranding: customizationMode === 'default', customizationMode });
      const items = [{ id: selected.id, quantity: quantity(item.quantity), properties }];
      for (const extra of snapshot.accessories || []) {
        if (extra.customText && extra.module !== 'backplate') throw new Error('Custom text is available on backplates.');
        const chosen = variant(moduleKey(extra));
        const part = catalog.parts.find(v => v.id === extra.module) || { label: { 'chin-long': 'Extended Chin', magsafe: 'MagSafe Ring' }[extra.module] };
        if (!part.label) throw new Error('Please check your extra module selection.');
        const details = { Model: 'Titan 2 Elite', Module: part.label };
        const chosenColor = extra.module === 'magsafe' ? null : color(extra.color);
        if (chosenColor) details.Color = chosenColor.label;
        const extraText = extra.customText ? textStyle(extra.customText) : null;
        if (extraText) addTextProperties(details, extraText);
        details._smallsmore_mcase_module = JSON.stringify({ schemaVersion: 1, sku: chosen.sku, module: extra.module, color: chosenColor, text: extraText });
        items.push({ id: chosen.id, quantity: quantity(extra.quantity), properties: details });
      }
      const total = items.reduce((sum, item) => {
        const unit = Array.from(variants.values()).find(v => v.id === item.id).price;
        const next = sum + unit * item.quantity;
        if (!Number.isSafeInteger(next)) throw new Error('Please reduce the selected quantity.');
        return next;
      }, 0);
      return { items, total, currency };
    };
    const canOrder = snapshot => runtime.storefront === true && build(snapshot).items.every(item => Array.from(variants.values()).some(v => v.id === item.id && v.available === true));
    const money = cents => Number.isSafeInteger(cents)
      ? currency === 'USD' ? `US$${(cents / 100).toFixed(2)}` : new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'code' }).format(cents / 100)
      : 'Price pending';
    const add = async (snapshot, fetcher = window.fetch.bind(window)) => {
      const payload = build(snapshot);
      if (!canOrder(snapshot)) throw new Error('Ordering opens soon. Your choices are saved.');
      let response;
      try {
        response = await fetcher(runtime.routes.cartAdd, { method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(20000), headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: payload.items }) });
      } catch {
        throw new Error('The cart could not be confirmed. Check your cart before trying again.');
      }
      let result;
      try { result = await response.json(); } catch { throw new Error('The cart could not be confirmed. Check your cart before trying again.'); }
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'These items could not be added. Your choices are saved.');
      if (!Array.isArray(result.items) || payload.items.some(line => !result.items.some(saved => Number(saved.variant_id || saved.id) === line.id && Object.entries(line.properties).every(([key, value]) => saved.properties?.[key] === value)))) throw new Error('The cart could not be confirmed. Check your cart before trying again.');
      return { payload, result };
    };
    const casePrices = style => ({ standard: variant(`case:${style}:standard`).price, custom: variant(`case:${style}:custom`).price });
    return { build, add, canOrder, money, variant, casePrices, caseKey, moduleKey, currency, storefront: runtime.storefront === true, routes: runtime.routes, images: catalog.images || {} };
  };
  window.MCaseCommerce = { create };
})();
